import { loadBaseCSS } from '../shared/button-utils.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * Persistent field composition with arbitrary prefix/suffix content or actions.
 * @element qds-input-group
 * @attr {string} label - Optional accessible group name; the field still needs its own label.
 * @slot prefix - Unit, currency, icon or action before the field.
 * @slot - Native or QDS field; retains its form owner, value, validation and accessible name.
 * @slot suffix - Unit or keyboard-operable action after the field.
 * @csspart group - Flexible layout, optionally a named accessible group.
 * @csspart prefix - Prefix wrapper; hidden when empty.
 * @csspart control - Flexible field wrapper.
 * @csspart suffix - Suffix wrapper; hidden when empty.
 */
export class QdsInputGroup extends HTMLElement {
  static get observedAttributes():string[] { return ['label']; }
  private _onSlotChange=():void=>this._sync();
  constructor(){
    super();
    this.attachShadow({mode:'open'}).innerHTML=`<style>${primitiveStyles}
      :host { display:block; min-width:0; }
      [part=group] { display:flex; align-items:center; gap:.5rem; min-width:0; }
      [part=control] { flex:1 1 auto; min-width:0; }
      [part=prefix], [part=suffix] { display:flex; align-items:center; flex:0 0 auto; }
      slot:not([name])::slotted(*) { box-sizing:border-box; display:block; min-width:0; max-width:100%; width:100%; }
      slot:not([name])::slotted(input), slot:not([name])::slotted(select), slot:not([name])::slotted(textarea) {
        min-height:44px; padding:.625rem; font:inherit; color:var(--theme-text-primary,#333);
        background:var(--theme-background-primary,white); border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:4px;
      }
      slot:not([name])::slotted(input:focus-visible), slot:not([name])::slotted(select:focus-visible), slot:not([name])::slotted(textarea:focus-visible) {
        outline:2px solid var(--theme-accent-primary,#445e89); outline-offset:2px;
      }
      slot[name]::slotted(button), slot[name]::slotted(a) { min-height:44px; min-width:44px; }
      slot[name]::slotted(button) { font:inherit; color:var(--theme-text-primary,#333); background:var(--theme-background-secondary,#f9fafb);
        border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:4px; }
    </style><div part="group"><span part="prefix"><slot name="prefix"></slot></span><span part="control"><slot></slot></span><span part="suffix"><slot name="suffix"></slot></span></div>`;
    loadBaseCSS();
  }
  get label():string { return this.getAttribute('label')||''; }
  set label(value:string){this.setAttribute('label',value??'');}
  connectedCallback():void{
    if(Object.prototype.hasOwnProperty.call(this,'label')){
      const value=this.label;Reflect.deleteProperty(this,'label');this.label=value;
    }
    this.shadowRoot!.addEventListener('slotchange',this._onSlotChange);this._sync();
  }
  disconnectedCallback():void{this.shadowRoot!.removeEventListener('slotchange',this._onSlotChange);}
  attributeChangedCallback():void{if(this.isConnected)this._sync();}
  private _sync():void{
    const root=this.shadowRoot!,group=root.querySelector('[part=group]')!;
    if(this.label){group.setAttribute('role','group');group.setAttribute('aria-label',this.label);}
    else{group.removeAttribute('role');group.removeAttribute('aria-label');}
    for(const name of ['prefix','suffix']){
      const slot=root.querySelector<HTMLSlotElement>(`slot[name=${name}]`)!;
      const populated=slot.assignedElements({flatten:true}).length>0;
      root.querySelector<HTMLElement>(`[part=${name}]`)!.hidden=!populated;
    }
  }
}
if(!customElements.get('qds-input-group'))customElements.define('qds-input-group',QdsInputGroup);
